import { Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import { useCallback } from 'react';
import styled from 'styled-components';

import { TranslationsConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/TranslationsEditor';
import { getHumanReadableStepName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import TranslationInputs from 'dashboard/apps/InquiryTemplateEditor/TranslationInputs';
import Banner from 'design-system/components/Banner/Banner';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';

const FieldsContainer = styled.div`
  width: 100%;

  ${Label} {
    display: inline-block;
    width: 260px;
    line-height: 22px;
    overflow: hidden;
    white-space: normal;
    word-wrap: pre-wrap;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const StyledButton = styled(Button)`
  width: initial;
`;

const StyledAlertBanner = styled(Banner).attrs({ variant: 'alert' })`
  padding-top: ${Spacing[1]};
  padding-bottom: ${Spacing[1]};
`;

type TranslationEditButtonProps = {
  openTranslationsPanel: () => void;
  editable: boolean;
};
const TranslationEditButton = ({
  openTranslationsPanel,
  editable,
  ...rest
}: TranslationEditButtonProps) => (
  <StyledButton
    {...rest}
    id='component__edit-translations-button'
    data-cy='component__edit-translations-button'
    disabled={!editable}
    onClick={openTranslationsPanel}
    type='button'
    width='button'
    variant='basic'
  >
    Edit translations
  </StyledButton>
);

interface TranslationEditSectionProps {
  config: TranslationsConfig;
  // label used for multi-translation components. otherwise defaults to 'Text'
  label?: string;
  enabledLocales: Array<InquiryIso6391LanguageCode>;
  currentLocale: InquiryIso6391LanguageCode;
  disabledWarning?: string;
  editable: boolean;
  getStepLocalizedStrings: (locales?: Array<InquiryIso6391LanguageCode>) => Promise<void>;
  handleOpenPanel: (config: TranslationsConfig) => void;
  handleTranslationUpdates: (translationUpdates: Record<string, any>) => void;
  onChange?: (value: string, locale: InquiryIso6391LanguageCode, path: string) => void;
}

export const TranslationEditSection = ({
  config,
  label,
  enabledLocales,
  currentLocale,
  disabledWarning,
  editable,
  getStepLocalizedStrings,
  handleOpenPanel,
  handleTranslationUpdates,
}: TranslationEditSectionProps) => {
  const fieldLabel = label ? getHumanReadableStepName(label) : 'Text';
  const openTranslationsPanel = useCallback(
    () => handleOpenPanel(config),
    [config, handleOpenPanel],
  );

  return (
    <FieldsContainer>
      <TranslationInputs
        config={config}
        label={fieldLabel}
        currentLocale={currentLocale}
        enabledLocales={[currentLocale]}
        editable={editable}
        handleTranslationUpdates={handleTranslationUpdates}
        getStepLocalizedStrings={getStepLocalizedStrings}
      />
      {!editable && disabledWarning != null && (
        <StyledAlertBanner>
          <CircleAlertIcon />
          {disabledWarning}
        </StyledAlertBanner>
      )}
      <Spacer height={1.5} />
      {enabledLocales?.filter((locale) => locale !== currentLocale).length > 0 && (
        <TranslationEditButton openTranslationsPanel={openTranslationsPanel} editable={editable} />
      )}
    </FieldsContainer>
  );
};

export default TranslationEditSection;
